import React, { useState } from 'react';
import {
  Button,
  Label,
  Modal,
  Spinner,
  Textarea,
  TextInput,
  Select,
  Checkbox,
} from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import { useCreateQuestionnaireMutation } from '../../redux/api/questionnaireApi';
import { IMultiLanguageValue } from '../../common/interfaces/MultiLanguageValue.interface';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { languageOptions } from '../../common/constants/languageOptions';

// Custom Components
import { LanguageDropdown } from '../Shared/LanguageSelectionDropdown/LanguageDropdown';
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const QuestionnaireCreateModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(languageOptions[0]);
  const [createQuestionnaire, { isLoading }] = useCreateQuestionnaireMutation();
  const defaultLang = i18n.language || 'ar';
  const isDarkMode = useAppSelector(selectIsDarkMode);

  const [body, setBody] = useState<IMultiLanguageValue[]>([
    { language: defaultLang, value: '' },
  ]);
  const [questions, setQuestions] = useState([
    {
      body: [{ language: defaultLang, value: '' }],
      options: [
        {
          body: [{ language: defaultLang, value: '' }],
          type: 'Option',
          questionId: '',
        },
      ],
      isFinalQuestion: false,
    },
  ]);
  // Get field value for specific language
  const getFieldValue = (
    fieldData: IMultiLanguageValue[],
    languageCode: string
  ) => {
    return (
      fieldData.find((item) => item.language === languageCode)?.value || ''
    );
  };
  // Update field value for specific language
  const updateFieldValue = (
    fieldData: IMultiLanguageValue[],
    languageCode: string,
    value: string
  ) => {
    const existingIndex = fieldData.findIndex(
      (item) => item.language === languageCode
    );
    const newData = [...fieldData];

    if (existingIndex >= 0) {
      newData[existingIndex] = { language: languageCode, value };
    } else {
      newData.push({ language: languageCode, value });
    }

    return newData;
  };
  // Reset state when modal closes
  const resetForm = () => {
    setBody([{ language: defaultLang, value: '' }]);
    setQuestions([
      {
        body: [{ language: defaultLang, value: '' }],
        options: [
          {
            body: [{ language: defaultLang, value: '' }],
            type: 'Option',
            questionId: '',
          },
        ],
        isFinalQuestion: false,
      },
    ]);
    setSelectedLanguage(languageOptions[0]);
  };

  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      {
        body: [{ language: defaultLang, value: '' }],
        options: [
          {
            body: [{ language: defaultLang, value: '' }],
            type: 'Option',
            questionId: '',
          },
        ],
        isFinalQuestion: false,
      },
    ]);
  };

  const handleRemoveQuestion = (qIndex: number) => {
    if (questions.length > 1) {
      const newQuestions = questions.filter((_, index) => index !== qIndex);
      setQuestions(newQuestions);
    }
  };

  const handleAddOption = (qIndex: number) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].options.push({
      body: [{ language: defaultLang, value: '' }],
      type: 'Option',
      questionId: '',
    });
    setQuestions(newQuestions);
  };

  const handleRemoveOption = (qIndex: number, oIndex: number) => {
    const newQuestions = [...questions];
    if (newQuestions[qIndex].options.length > 1) {
      newQuestions[qIndex].options = newQuestions[qIndex].options.filter(
        (_, index) => index !== oIndex
      );
      setQuestions(newQuestions);
    }
  };
  const handleBodyChange = (languageCode: string, value: string) => {
    setBody(updateFieldValue(body, languageCode, value));
  };
  const handleQuestionBodyChange = (
    qIndex: number,
    languageCode: string,
    value: string
  ) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].body = updateFieldValue(
      newQuestions[qIndex].body,
      languageCode,
      value
    );
    setQuestions(newQuestions);
  };
  // Handle option body change
  const handleOptionBodyChange = (
    qIndex: number,
    oIndex: number,
    languageCode: string,
    value: string
  ) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].options[oIndex].body = updateFieldValue(
      newQuestions[qIndex].options[oIndex].body,
      languageCode,
      value
    );
    setQuestions(newQuestions);
  };
  // Handle questionId change for option
  const handleQuestionIdChange = (
    qIndex: number,
    oIndex: number,
    questionId: string
  ) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].options[oIndex].questionId = questionId;
    setQuestions(newQuestions);
  };

  // Handle isFinalQuestion change
  const handleIsFinalQuestionChange = (qIndex: number, checked: boolean) => {
    const newQuestions = [...questions];
    newQuestions[qIndex].isFinalQuestion = checked;
    setQuestions(newQuestions);
  };

  // Handle submit
  const handleSubmit = async () => {
    const payload = {
      body,
      questions: questions.map((q, qIndex) => ({
        id: qIndex,
        body: q.body,
        options: q.options.map((opt, oIndex) => ({
          id: oIndex,
          body: opt.body,
          type: opt.type,
          questionId: opt.questionId || '',
        })),
        isFinalQuestion: q.isFinalQuestion,
      })),
    };

    try {
      await createQuestionnaire(payload).unwrap();
      resetForm();
      onClose();
    } catch (err) {
      // The toast middleware reports the failure; the modal stays open with the input.
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Button disabled if any required field is empty
  const currentLang = selectedLanguage.languageCode;

  const isSaveDisabled =
    isLoading ||
    !getFieldValue(body, currentLang).trim() ||
    questions.some((q) => !getFieldValue(q.body, currentLang).trim()) ||
    questions.some((q) =>
      q.options.some((opt) => !getFieldValue(opt.body, currentLang).trim())
    );

  return (
    <Modal show={isOpen} onClose={handleClose}>
      <Modal.Header className="modal-header">
        {t('questionnaireLabels.addNew')}
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-6">
          <div className="mb-4">
            <Label
              value={t('questionnaireLabels.selectQuestionnaireLanguage')}
              className="mb-2"
            />
            <LanguageDropdown
              selectedLanguage={selectedLanguage}
              onSelectLanguage={setSelectedLanguage}
              isDarkMode={isDarkMode}
            />
          </div>
          <div>
            <label className="font-semibold">
              {t('questionnaireLabels.body') +
                ' (' +
                selectedLanguage.label +
                ')'}
              <span className="ms-1 text-red-500">*</span>
            </label>
            <Textarea
              rows={4}
              className="mt-2 w-full rounded-lg border border-gray-300 p-2"
              value={getFieldValue(body, selectedLanguage.languageCode)}
              onChange={(e) =>
                handleBodyChange(selectedLanguage.languageCode, e.target.value)
              }
              disabled={isLoading}
            />
          </div>

          {/* Questions */}
          <div>
            <h2 className="font-semibold">
              {t('questionnaireLabels.questions')} ({selectedLanguage.label})
              <span className="ms-1 text-red-500">*</span>
            </h2>
            {questions.map((q, qIndex) => (
              <div key={qIndex} className="mt-4 rounded-lg border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-medium">
                    {t('questionnaireLabels.enterQuestion')} {qIndex + 1} (
                    {selectedLanguage.label})
                  </span>
                  {questions.length > 1 && (
                    <Button
                      size="xs"
                      color="failure"
                      onClick={() => handleRemoveQuestion(qIndex)}
                      disabled={isLoading}
                    >
                      <RiDeleteBin6Line />
                    </Button>
                  )}
                </div>
                <TextInput
                  id={`question-${qIndex}-input`}
                  type="text"
                  className="mb-2 w-full rounded-lg border border-gray-300 p-2"
                  placeholder={`${t('questionnaireLabels.enterQuestion')} ${qIndex + 1} (${selectedLanguage.label})`}
                  value={getFieldValue(q.body, selectedLanguage.languageCode)}
                  onChange={(e) =>
                    handleQuestionBodyChange(
                      qIndex,
                      selectedLanguage.languageCode,
                      e.target.value
                    )
                  }
                  disabled={isLoading}
                />

                {/* isFinalQuestion Checkbox */}
                <div className="mb-3 flex items-center gap-2">
                  <Checkbox
                    id={`is-final-question-${qIndex}`}
                    checked={q.isFinalQuestion}
                    onChange={(e) =>
                      handleIsFinalQuestionChange(qIndex, e.target.checked)
                    }
                    disabled={isLoading}
                  />
                  <Label htmlFor={`is-final-question-${qIndex}`}>
                    {t('questionnaireLabels.isFinalQuestion')}
                  </Label>
                </div>

                <div className="ml-3">
                  <h3 className="font-medium">
                    {t('questionnaireLabels.options')}
                  </h3>
                  {q.options.map((opt, oIndex) => (
                    <div key={oIndex} className="mt-2">
                      <div className="flex gap-2">
                        <TextInput
                          id={`option-${oIndex}-input`}
                          type="text"
                          className="w-full rounded-lg border border-gray-200 p-2"
                          placeholder={`${t('questionnaireLabels.enterOption')} ${
                            oIndex + 1
                          }`}
                          value={getFieldValue(
                            opt.body,
                            selectedLanguage.languageCode
                          )}
                          onChange={(e) =>
                            handleOptionBodyChange(
                              qIndex,
                              oIndex,
                              selectedLanguage.languageCode,
                              e.target.value
                            )
                          }
                          disabled={isLoading}
                        />
                        {q.options.length > 1 && (
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => handleRemoveOption(qIndex, oIndex)}
                            disabled={isLoading}
                          >
                            <RiDeleteBin6Line className="m-auto" />
                          </Button>
                        )}
                      </div>

                      {questions.length > 1 && (
                        <div className="select-next-question mt-2">
                          <Label
                            htmlFor={`option-${qIndex}-${oIndex}-next-question`}
                            value={
                              t('questionnaireLabels.nextQuestion') ||
                              'Navigate to question (optional)'
                            }
                            className="text-sm text-gray-600"
                          />
                          <Select
                            id={`option-${qIndex}-${oIndex}-next-question`}
                            value={opt.questionId || ''}
                            onChange={(e) =>
                              handleQuestionIdChange(
                                qIndex,
                                oIndex,
                                e.target.value
                              )
                            }
                            disabled={isLoading}
                            className="mt-1"
                          >
                            <option value="">--</option>
                            {/* hide current question */}
                            {questions.map((_, idx) => (
                              <option
                                key={idx}
                                value={String(idx)}
                                className={`${idx === qIndex ? 'hidden' : ''}`}
                              >
                                {t('questionnaireLabels.goToQuestion') ||
                                  'Go to question'}{' '}
                                {idx + 1}
                              </option>
                            ))}
                          </Select>
                        </div>
                      )}
                    </div>
                  ))}
                  <Button
                    size="xs"
                    className="btn-light mt-2"
                    onClick={() => handleAddOption(qIndex)}
                    disabled={isLoading}
                  >
                    + {t('questionnaireLabels.addOption')}
                  </Button>
                </div>
              </div>
            ))}
            <Button
              className="btn-light mt-3"
              size="xs"
              onClick={handleAddQuestion}
              disabled={isLoading}
            >
              + {t('questionnaireLabels.addOtherQuestion')}
            </Button>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex w-full items-center justify-between">
          <Button
            color="green"
            onClick={handleSubmit}
            disabled={isSaveDisabled}
          >
            {isLoading ? (
              <>
                <Spinner size="sm" aria-label="Info spinner example" light />
              </>
            ) : (
              t('content.save')
            )}
          </Button>
          <Button color="gray" onClick={handleClose} disabled={isLoading}>
            {t('content.cancel')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
